import {
  CheckboxIcon,
  CheckCircledIcon,
  DotsHorizontalIcon,
  EnvelopeClosedIcon,
  FileTextIcon,
  LightningBoltIcon,
  PauseIcon,
  Pencil1Icon,
  Pencil2Icon,
  PersonIcon,
  PlayIcon,
  TrashIcon,
  ViewVerticalIcon,
} from "@radix-ui/react-icons";
import * as Popover from "@radix-ui/react-popover";
import IconTip from "components/Dashboard/PeoplePanel/IconTip";
import "components/Dashboard/PeoplePanel/index.css";
import IconBubble, { BubbleTone } from "components/IconBubble";
import PersonPill from "components/PersonPill";
import {
  Fragment,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import TriggerStack from "./AutomationsTabTrigger";
import { RunsChip, TimingChip, WatchingChip } from "./AutomationsTabTiming";
import { useReference } from "./hooks";
import { facetsOf, statusLabel } from "./kinds";
import { closedTaskStep, isSubjects, stepProblems } from "./model";
import { AddButton } from "./parts";
import {
  columnById,
  fieldByRef,
  ReferenceData,
  templateById,
} from "./reference";
import type { Automation, Position, Step, StepAction } from "./types";
import {
  askChip,
  columnCountsOf,
  columnWords,
  LaneColumn,
  laneColumns,
  taskTitle,
} from "./AutomationsTabModel";
import {
  ACTION_LABELS,
  assignPhrase,
  capitalize,
  channelWords,
  dueWords,
  fieldValueWords,
  noteWords,
  sendToWords,
  subjectNames,
  subjectsWords,
  tutorPoolWords,
  tutorShareWords,
} from "./words";

const ACTION_ICONS: Record<StepAction, ReactNode> = {
  message: <EnvelopeClosedIcon />,
  task: <CheckboxIcon />,
  crm_move: <ViewVerticalIcon />,
  crm_field: <Pencil2Icon />,
  close_task: <CheckCircledIcon />,
  assign_tutor: <PersonIcon />,
  add_note: <FileTextIcon />,
};

export function ActionIcon({ action }: { action: StepAction }) {
  return (
    <span
      className="inline-flex flex-none [&>svg]:h-3.5 [&>svg]:w-3.5"
      aria-hidden
    >
      {ACTION_ICONS[action]}
    </span>
  );
}

/**
 * What a lane asks of the page: turn it on or off, rename it, open the
 * step dialog for a step, a new one or a new gate (anchor: what opened
 * it, which gets the focus back), change a column's timing, or delete it.
 */
export interface LaneActions {
  toggle: (automation: Automation, on: boolean) => void;
  openStep: (automation: Automation, step: Step, anchor: HTMLElement) => void;
  /** Another action at the same moment, stacked in the column. */
  addToColumn: (
    automation: Automation,
    column: LaneColumn,
    anchor: HTMLElement
  ) => void;
  /** The next step, after the lane's last column: the dialog asks When. */
  addNext: (automation: Automation, anchor: HTMLElement) => void;
  /** A trigger gate after the lane's last column, and the step right after it. */
  addGate: (automation: Automation, anchor: HTMLElement) => void;
  /** A new name, from the ⋯ menu's Rename. */
  rename: (automation: Automation, name: string) => Promise<unknown>;
  /** A column's timing changed: every step in it moves. */
  retime: (
    automation: Automation,
    column: LaneColumn,
    position: Position
  ) => void;
  /** Delete the automation, after a confirm. */
  remove: (automation: Automation) => void;
}

// ── A card ────────────────────────────────────────────────────────────────

/**
 * A card's title: the task's name in words, the template, the column or
 * the field; the task it closes (steps: the lane's), the tutors it assigns
 * from, or the note.
 */
export function plainTitle(
  ref: ReferenceData,
  step: Step,
  steps?: Step[]
): string {
  switch (step.action) {
    case "message":
      return (
        templateById(ref, step.message.templateId)?.name ?? "No template picked"
      );
    case "task":
      return taskTitle(ref, step.task.name);
    case "crm_move":
      return (
        columnById(ref, step.crmMove.boardId, step.crmMove.columnId)?.column
          .name ?? "No column picked"
      );
    case "crm_field":
      return isSubjects(step.crmField)
        ? "Subjects"
        : fieldByRef(ref, step.crmField.field)?.label ?? "No field picked";
    case "close_task": {
      if (!steps) return ACTION_LABELS.close_task;
      const task = closedTaskStep(steps, step.closeTask.stepId);
      return task ? taskTitle(ref, task.task.name) : "No task picked";
    }
    case "assign_tutor":
      return tutorPoolWords(ref, step.assignTutor);
    case "add_note":
      return noteWords(ref, step.note.text) || "No note written";
  }
}

function whoOf(ref: ReferenceData, automation: Automation, step: Step) {
  switch (step.action) {
    case "message":
      return `To ${sendToWords(step.message.sendTo)}, ${channelWords(
        step.message
      )}.`;
    case "task":
      return `${capitalize(
        assignPhrase(ref, step.task.assignTo, facetsOf(ref, automation.trigger))
      )}, ${dueWords(step.task.due)}.`;
    case "crm_move": {
      const found = columnById(
        ref,
        step.crmMove.boardId,
        step.crmMove.columnId
      );
      return found ? `On ${found.board.name}.` : "";
    }
    case "crm_field": {
      const config = step.crmField;
      if (isSubjects(config))
        return config.subject_ids.length
          ? `${subjectsWords(
              config.mode,
              subjectNames(ref, config.subject_ids),
              "their"
            )}.`
          : "";
      const field = fieldByRef(ref, config.field);
      return field
        ? `Set to ${fieldValueWords(ref, field, config.value)}.`
        : "";
    }
    case "close_task":
      return `Mark as ${statusLabel(step.closeTask.status)}.`;
    case "assign_tutor":
      return `${tutorShareWords(step.assignTutor)}.`;
    case "add_note":
      return "On their record.";
  }
}

const stop =
  (fn: (e: MouseEvent<HTMLElement>) => void) =>
  (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    fn(e);
  };

function StepCard({
  automation,
  step,
  editing,
  actions,
}: {
  automation: Automation;
  step: Step;
  editing: boolean;
  actions: LaneActions;
}) {
  const ref = useReference();
  const title = plainTitle(ref, step, automation.steps);
  const who = whoOf(ref, automation, step);
  const problem = stepProblems(ref, automation, step)[0];
  const card = (e: MouseEvent<HTMLElement>) =>
    (e.currentTarget.closest("article") as HTMLElement) ?? e.currentTarget;
  const template =
    step.action === "message"
      ? templateById(ref, step.message.templateId)
      : null;

  return (
    <article
      aria-label={`${ACTION_LABELS[step.action]}: ${title}`}
      aria-current={editing || undefined}
      data-step-id={step.id}
      onClick={(e) => actions.openStep(automation, step, e.currentTarget)}
      className={`group cursor-pointer rounded-lg border p-3 transition-shadow ${
        editing
          ? "border-green-500 ring-2 ring-green-500/20"
          : "border-gray-200 hover:border-gray-300"
      } ${automation.on ? "bg-white" : "bg-neutral-50"}`}
    >
      <div className="flex items-center gap-2">
        <span className="flex min-w-0 flex-1 items-center gap-1.5 text-xs font-semibold text-gray-600">
          <ActionIcon action={step.action} />
          <span className="truncate">{ACTION_LABELS[step.action]}</span>
        </span>
        {/* Quiet until the card is pointed at or has focus. */}
        <IconTip label="Edit step">
          <button
            type="button"
            className="people-panel__icon -my-1.5 -mr-1.5 opacity-0 focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100"
            aria-label="Edit step"
            onClick={stop((e) => actions.openStep(automation, step, card(e)))}
          >
            <Pencil1Icon />
          </button>
        </IconTip>
      </div>
      <button
        type="button"
        data-card-title
        className={`mt-1.5 block w-full break-words text-left text-sm font-semibold leading-5 ${
          automation.on ? "text-gray-900" : "text-gray-500"
        }`}
        onClick={stop((e) => actions.openStep(automation, step, card(e)))}
      >
        {title}
      </button>
      {who && (
        <p className="mt-0.5 break-words text-[13px] leading-[18px] text-gray-600">
          {who}
        </p>
      )}
      {step.askFirst.on && (
        <div className="mt-2">
          <PersonPill color="orange" label={askChip(step)} />
        </div>
      )}
      {problem && <p className="mt-1.5 text-xs text-red-600">{problem}</p>}
      {/* A message's wording is its template's: editing the template
          changes the message everywhere the template is used. */}
      {template && (
        <div className="mt-2.5 border-t border-gray-100 pt-2 text-xs">
          <Link
            to={`/message-templates/${template.id}`}
            className="font-semibold text-green-700 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            Edit template
          </Link>
        </div>
      )}
    </article>
  );
}

// ── The track: markers and the lines between them ─────────────────────────

/** Into a gate the line is dashed (it waits for it); otherwise it is solid (time passing). */
function TrackLine({ into }: { into: "time" | "gate" | "end" }) {
  return into === "gate" ? (
    <span className="absolute inset-x-0 top-1/2 border-t border-dashed border-gray-400" />
  ) : (
    <span
      className={`absolute inset-x-0 top-1/2 h-px ${
        into === "end" ? "bg-gray-200" : "bg-gray-300"
      }`}
    />
  );
}

/** The trigger is a green dot; a time is a green ring; a gate is a gray diamond. */
function Marker({
  kind,
  on,
}: {
  kind: "trigger" | "time" | "gate";
  on: boolean;
}) {
  if (kind === "trigger")
    return (
      <span
        className={`relative h-3 w-3 flex-none rounded-full ${
          on ? "bg-green-500" : "bg-gray-400"
        }`}
      />
    );
  return kind === "time" ? (
    <span
      className={`relative h-3 w-3 flex-none rounded-full border-2 bg-white ${
        on ? "border-green-500" : "border-gray-400"
      }`}
    />
  ) : (
    <span className="relative ml-0.5 h-2.5 w-2.5 flex-none rotate-45 border-2 border-gray-600 bg-white" />
  );
}

/**
 * The lane's entry: Watching for where a step column has its timing, the
 * room for counts when the lane has any, and its trigger cards, so the cards
 * line up.
 */
function TriggerColumn({
  automation,
  next,
  counted,
}: {
  automation: Automation;
  next: "time" | "gate" | "end";
  counted: boolean;
}) {
  return (
    <li className="w-60 flex-none" aria-label="Trigger">
      <div className="relative flex h-6 items-center" aria-hidden>
        <span className="absolute inset-y-0 left-3 right-0">
          <TrackLine into={next} />
        </span>
        <Marker kind="trigger" on={automation.on} />
      </div>
      <div className="mt-1 pr-4">
        <WatchingChip />
      </div>
      {counted && <div className="mt-1 h-4" aria-hidden />}
      <div className="mt-2">
        <TriggerStack automation={automation} gate={null} />
      </div>
    </li>
  );
}

/**
 * A gate: Watching for where a step column has its timing, the people
 * waiting at it ("3 waiting here"), and its trigger cards.
 */
function GateColumn({
  automation,
  column,
  next,
  counts,
  counted,
}: {
  automation: Automation;
  column: LaneColumn;
  next: "time" | "gate" | "end";
  counts: string;
  counted: boolean;
}) {
  const ref = useReference();
  if (!column.gate) return null;
  return (
    <li
      className="w-60 flex-none"
      aria-label={`Wait: ${columnWords(ref, automation, column)}`}
    >
      <div className="relative flex h-6 items-center" aria-hidden>
        <span className="absolute inset-y-0 left-3 right-0">
          <TrackLine into={next} />
        </span>
        <Marker kind="gate" on={automation.on} />
      </div>
      <div className="mt-1 pr-4">
        <WatchingChip />
      </div>
      {counted && (
        <p className="mt-1 min-h-[1rem] break-words pr-4 text-xs leading-4 text-gray-500">
          {counts}
        </p>
      )}
      <div className="mt-2">
        <TriggerStack automation={automation} gate={column.gate} />
      </div>
    </li>
  );
}

/**
 * A column: its timing's chip, what it has going now (counts: "2
 * scheduled, 1 waiting for approval"; a line kept empty when another
 * column in the lane has some, so the cards line up), and its steps.
 */
function Column({
  automation,
  column,
  next,
  counts,
  counted,
  editingStepId,
  actions,
}: {
  automation: Automation;
  column: LaneColumn;
  next: "time" | "gate" | "end";
  counts: string;
  counted: boolean;
  editingStepId: string | null;
  actions: LaneActions;
}) {
  const ref = useReference();
  const label = columnWords(ref, automation, column);
  return (
    <li className="w-60 flex-none" aria-label={label}>
      <div className="relative flex h-6 items-center" aria-hidden>
        <span className="absolute inset-y-0 left-3 right-0">
          <TrackLine into={next} />
        </span>
        <Marker kind={column.kind} on={automation.on} />
      </div>
      <div className="mt-1 flex min-w-0 items-start gap-1 pr-4">
        <RunsChip />
        <TimingChip
          label={label}
          automation={automation}
          column={column}
          onPick={(position) => actions.retime(automation, column, position)}
        />
      </div>
      {counted && (
        <p className="mt-1 min-h-[1rem] break-words pr-4 text-xs leading-4 text-gray-500">
          {counts}
        </p>
      )}
      <div className="mt-2 space-y-2">
        {column.steps.map((step) => (
          <StepCard
            key={step.id}
            automation={automation}
            step={step}
            editing={step.id === editingStepId}
            actions={actions}
          />
        ))}
      </div>
      <div className="mt-2 flex">
        <AddButton
          label="Add additional action"
          ariaLabel={`Add additional action: ${label}`}
          onAdd={(anchor) => actions.addToColumn(automation, column, anchor)}
        />
      </div>
    </li>
  );
}

/** The gap between two columns: the line, and before a gate a dashed rule down the lane. */
function Gap({ into }: { into: "time" | "gate" }) {
  return (
    <li
      className="flex w-10 flex-none flex-col items-center self-stretch"
      aria-hidden
    >
      <div className="relative flex h-6 w-full items-center justify-center">
        <TrackLine into={into} />
      </div>
      {into === "gate" && (
        <span className="mt-1 w-0 flex-1 border-l border-dashed border-gray-300" />
      )}
    </li>
  );
}

const TITLE =
  "-ml-2 max-w-[32rem] rounded-md px-2 text-[17px] font-bold leading-8 text-[#3d4fb0]";

/**
 * The name as a field, for the ⋯ menu's Rename, focused and selected: Enter
 * or leaving the field saves, Escape keeps the name.
 */
function NameField({
  automation,
  actions,
  onDone,
}: {
  automation: Automation;
  actions: LaneActions;
  onDone: () => void;
}) {
  const [value, setValue] = useState(automation.name);
  const input = useRef<HTMLInputElement>(null);
  const done = useRef(false);

  useEffect(() => {
    input.current?.focus();
    input.current?.select();
  }, []);

  const finish = (keep: boolean) => {
    if (done.current) return;
    done.current = true;
    const next = value.trim();
    if (keep && next && next !== automation.name)
      actions.rename(automation, next);
    onDone();
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      finish(true);
    }
    if (e.key === "Escape") {
      e.preventDefault();
      finish(false);
    }
  };

  return (
    <input
      ref={input}
      type="text"
      aria-label="Automation name"
      maxLength={100}
      className={`${TITLE} bg-white outline-none ring-1 ring-indigo-200 focus:ring-2`}
      style={{ width: `${Math.max(value.length, 12) + 4}ch` }}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={onKey}
      onBlur={() => finish(true)}
    />
  );
}

/**
 * The automation's name, the title of its lane's header, with Paused beside
 * it while it is off: plain text, or its field while it is being renamed.
 */
function NamePill({
  automation,
  name,
  renaming,
  onRenamed,
  actions,
}: {
  automation: Automation;
  name: string;
  renaming: boolean;
  onRenamed: () => void;
  actions: LaneActions;
}) {
  return (
    <div className="flex h-8 min-w-0 items-center gap-2">
      {renaming ? (
        <NameField
          automation={automation}
          actions={actions}
          onDone={onRenamed}
        />
      ) : (
        <div title={name} className={`${TITLE} flex min-w-0 items-center`}>
          <LightningBoltIcon className="mr-1.5 h-4 w-4 flex-none" aria-hidden />
          <span className="truncate">{name}</span>
        </div>
      )}
      {!automation.on && (
        // A long name gives way first: Paused always reads whole.
        <span className="flex flex-none">
          <PersonPill color="gray" label="Paused" />
        </span>
      )}
    </div>
  );
}

/** Activity's Done, for one automation. */
function historyHref(id: string) {
  return `/automations?tab=activity&automation=${encodeURIComponent(id)}`;
}

/**
 * The row's ⋯ menu, the one place to rename or pause it: Rename, Pause (Turn
 * on while it is paused, through the turn-on confirm), and Delete, which asks
 * first.
 */
function LaneMenu({
  automation,
  name,
  onRename,
  actions,
}: {
  automation: Automation;
  name: string;
  onRename: () => void;
  actions: LaneActions;
}) {
  const [open, setOpen] = useState(false);
  const renamePicked = useRef(false);
  const items: {
    label: string;
    icon: ReactNode;
    tone: BubbleTone;
    onSelect: () => void;
  }[] = [
    {
      label: "Rename",
      icon: <Pencil1Icon />,
      tone: BubbleTone.VIOLET,
      onSelect: () => {
        renamePicked.current = true;
        onRename();
      },
    },
    automation.on
      ? {
          label: "Pause",
          icon: <PauseIcon />,
          tone: BubbleTone.ORANGE,
          onSelect: () => actions.toggle(automation, false),
        }
      : {
          label: "Turn on",
          icon: <PlayIcon />,
          tone: BubbleTone.GREEN,
          onSelect: () => actions.toggle(automation, true),
        },
    {
      label: "Delete",
      icon: <TrashIcon />,
      tone: BubbleTone.RED,
      onSelect: () => actions.remove(automation),
    },
  ];
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="people-panel__icon"
          aria-label={`More for ${name}`}
        >
          <DotsHorizontalIcon />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="people-panel__menu"
          align="end"
          sideOffset={4}
          collisionPadding={12}
          // The name's field has the focus by now: the menu leaves it there.
          onCloseAutoFocus={(e: Event) => {
            if (renamePicked.current) e.preventDefault();
            renamePicked.current = false;
          }}
        >
          {items.map(({ label, icon, tone, onSelect }) => (
            <button
              key={label}
              type="button"
              className="people-panel__menu-item flex items-center gap-2.5 py-[0.35rem] pl-[0.4rem] pr-3"
              onClick={() => {
                setOpen(false);
                onSelect();
              }}
            >
              <IconBubble tone={tone} small>
                {icon}
              </IconBubble>
              {label}
            </button>
          ))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/**
 * A lane's timeline: wider than the page, it scrolls sideways in its own
 * row, with a soft fade at the right while more is hidden.
 */
function Track({ flash, children }: { flash: boolean; children: ReactNode }) {
  const scroller = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState(false);
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const check = () =>
      setMore(el.scrollWidth - el.scrollLeft - el.clientWidth > 1);
    check();
    el.addEventListener("scroll", check, { passive: true });
    const resized =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(check);
    resized?.observe(el);
    if (el.firstElementChild) resized?.observe(el.firstElementChild);
    return () => {
      el.removeEventListener("scroll", check);
      resized?.disconnect();
    };
  }, []);
  return (
    <div className="relative min-w-0 flex-initial">
      {/* Reaches into the lane's padding, so an add button's hover glow isn't cut off. */}
      <div ref={scroller} className="-mb-4 -ml-4 overflow-x-auto pb-5 pl-4">
        {children}
      </div>
      {more && (
        <span
          aria-hidden
          className={`pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l to-transparent transition-colors duration-700 ${
            flash ? "from-green-50" : "from-white"
          }`}
        />
      )}
    </div>
  );
}

// ── A lane ────────────────────────────────────────────────────────────────

/**
 * One automation, as its own row: a card holding its name's pill, then
 * its timeline, left to right: the trigger's cards, then each column (the
 * steps that happen at the same moment, stacked) and each gate (the
 * trigger cards a run waits for). Add next step and Add a trigger gate
 * stay at the timeline's end, and History and the ⋯ menu at the row's
 * right end, outside what scrolls.
 */
export function Lane({
  automation,
  editingStepId,
  flash,
  actions,
}: {
  automation: Automation;
  editingStepId: string | null;
  flash: boolean;
  actions: LaneActions;
}) {
  const [renaming, setRenaming] = useState(false);
  const columns = laneColumns(automation.steps, automation.gates);
  const counts = columnCountsOf(automation.stats.columns, columns);
  const counted = counts.some(Boolean);
  const name = automation.name || "Untitled automation";

  return (
    <div
      role="group"
      aria-label={name}
      data-automation-id={automation.id}
      className={`rounded-lg border border-gray-200 transition-colors duration-700 ${
        flash ? "bg-green-50" : "bg-white"
      }`}
    >
      <div className="flex items-center justify-between gap-6 rounded-t-lg border-b border-[#dfe3fb] bg-[#f3f5fe] px-5 py-1.5">
        <NamePill
          automation={automation}
          name={name}
          renaming={renaming}
          onRenamed={() => setRenaming(false)}
          actions={actions}
        />
        <div className="flex flex-none items-center gap-3">
          <Link
            to={historyHref(automation.id)}
            aria-label={`History of ${name}`}
            className="whitespace-nowrap text-xs font-semibold text-green-700 hover:underline"
          >
            History
          </Link>
          <LaneMenu
            automation={automation}
            name={name}
            onRename={() => setRenaming(true)}
            actions={actions}
          />
        </div>
      </div>

      <div className="p-5">
        <div className="flex min-w-0 items-start">
          <Track flash={flash}>
            <ol
              className="flex w-max items-start"
              aria-label={`Steps of ${name}`}
            >
              <TriggerColumn
                automation={automation}
                next={columns[0]?.kind ?? "end"}
                counted={counted}
              />
              {columns.length > 0 && <Gap into={columns[0].kind} />}
              {columns.map((column, i) => (
                <Fragment key={column.key}>
                  {column.kind === "gate" ? (
                    <GateColumn
                      automation={automation}
                      column={column}
                      next={columns[i + 1]?.kind ?? "end"}
                      counts={counts[i]}
                      counted={counted}
                    />
                  ) : (
                    <Column
                      automation={automation}
                      column={column}
                      next={columns[i + 1]?.kind ?? "end"}
                      counts={counts[i]}
                      counted={counted}
                      editingStepId={editingStepId}
                      actions={actions}
                    />
                  )}
                  {i < columns.length - 1 && <Gap into={columns[i + 1].kind} />}
                </Fragment>
              ))}
            </ol>
          </Track>
          <div className="flex h-6 flex-none items-center gap-2 pl-2">
            <AddButton
              label="Add next step"
              onAdd={(anchor) => actions.addNext(automation, anchor)}
            />
            <AddButton
              label="Add a trigger gate"
              onAdd={(anchor) => actions.addGate(automation, anchor)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
